import { usePlayerStore, formatTime } from "@/lib/player";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Play, Pause, Maximize2, X, Volume2, VolumeX } from "lucide-react";
import { cn } from "@/lib/utils";
import { useState, useEffect } from "react";
import { useTheme } from "@/components/ThemeProvider";
import { useBackendSignedUrl } from "@/hooks/useBackendSignedUrl";
import { NoArtwork } from "@/components/shared/NoArtwork";

export function MiniPlayer({
  audioRef,
}: {
  audioRef: React.RefObject<HTMLAudioElement>;
}) {
  const {
    currentTrack,
    isPlaying,
    position,
    duration,
    volume,
    isMuted,
    pauseTrack,
    resumeTrack,
    setPosition,
    setDuration,
    setVolume,
    toggleMute,
    toggleMinimized,
    isMinimized,
    clearTrack,
    toggleExpanded,
    stopCurrentTrack,
  } = usePlayerStore();

  const [currentTime, setCurrentTime] = useState(0);
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";

  // Get signed URL from backend using track ID (lazy loading - only fetch when needed).
  // Thread the track kind so reference tracks route to the reference-audio bucket (C1).
  const {
    signedUrl: signedAudioUrl,
    loading: urlLoading,
    error: urlError,
    fetchSignedUrl,
  } = useBackendSignedUrl(
    currentTrack?.id != null ? String(currentTrack.id) : null,
    false, // Disable auto-fetch
    currentTrack?.kind ? { kind: currentTrack.kind } : undefined
  );

  // Track the previous track ID to detect actual changes
  const [previousTrackId, setPreviousTrackId] = useState<string | null>(null);
  const [isStopping, setIsStopping] = useState(false);

  // Only stop current track when it's actually a different track
  useEffect(() => {
    if (
      audioRef.current &&
      currentTrack &&
      currentTrack.id !== previousTrackId
    ) {
      // Only stop if this is actually a new track (not just switching between players)
      const isNewTrack =
        previousTrackId !== null && currentTrack.id !== previousTrackId;

      if (isNewTrack) {
        if (!audioRef.current.ended) {
          // User-initiated track change: stop current audio aggressively to prevent overlap
          setIsStopping(true);
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
          audioRef.current.muted = true;
          audioRef.current.volume = 0;
          stopCurrentTrack();
          setTimeout(() => setIsStopping(false), 100);
        }
        // Auto-advance (audio.ended): skip aggressive stop — audio already finished,
        // isPlaying stays true so signed URL fetch and playback proceed naturally
        setPreviousTrackId(String(currentTrack.id));
      } else {
        // Just update the track ID without stopping audio
        setPreviousTrackId(String(currentTrack.id));
      }
    }
  }, [currentTrack?.id, previousTrackId, stopCurrentTrack]);

  // audioRef is now passed as a prop

  // Load new track and reset state only when we have signed URL
  useEffect(() => {
    if (audioRef.current && currentTrack && signedAudioUrl) {
      const audioUrl = signedAudioUrl;

      if (audioRef.current.src !== audioUrl) {
        // Store current playback state before changing source
        const wasPlaying = !audioRef.current.paused;
        const currentTime = audioRef.current.currentTime;

        audioRef.current.src = audioUrl;
        audioRef.current.load();

        // Restore volume and unmute for new track
        audioRef.current.muted = false;
        audioRef.current.volume = 1;

        // Only reset position for actual new tracks, not when switching between players
        // The position should be preserved from the player store
        setCurrentTime(0);
        setDuration(0);

        // Only play if isPlaying is true
        if (isPlaying) {
          audioRef.current.play().catch(() => {
            // Play failed, waiting for canplay event
          });
        }
      }
    }
  }, [currentTrack?.id, signedAudioUrl, isPlaying]); // Only load when we have signed URL

  // Handle play/pause state
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      // Fetch signed URL if not already available when play is requested
      if (!signedAudioUrl && !urlLoading && !urlError) {
        fetchSignedUrl();
        return; // Wait for signed URL to be fetched
      }

      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          // Only handle non-abort errors to avoid spam
          if (error.name !== "AbortError") {
            pauseTrack();
          }
        });
      }
    } else {
      audio.pause();

      // Immediately sync current time and position for UI
      const time = audio.currentTime;
      setCurrentTime(time);
      if (!isNaN(audio.duration)) {
        setPosition((time / audio.duration) * 100);
      }
    }
  }, [isPlaying, signedAudioUrl, urlLoading, urlError, fetchSignedUrl]);

  // Handle audio loading errors
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleError = () => {
      pauseTrack();
    };

    const handleCanPlay = () => {
      // Auto-play when audio is ready and we have a current track
      if (currentTrack && !isPlaying && !isStopping) {
        resumeTrack();
      }
    };

    const handleCanPlayThrough = () => {
      // Also try to play on canplaythrough for faster response
      if (currentTrack && !isPlaying && !isStopping) {
        resumeTrack();
      }
    };

    audio.addEventListener("error", handleError);
    audio.addEventListener("canplay", handleCanPlay);
    audio.addEventListener("canplaythrough", handleCanPlayThrough);

    return () => {
      audio.removeEventListener("error", handleError);
      audio.removeEventListener("canplay", handleCanPlay);
      audio.removeEventListener("canplaythrough", handleCanPlayThrough);
    };
  }, [
    signedAudioUrl,
    pauseTrack,
    currentTrack,
    isPlaying,
    resumeTrack,
    isStopping,
  ]);

  // Handle audio events
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => {
      const time = audio.currentTime;
      const dur = audio.duration;

      setCurrentTime(time);
      if (!isNaN(dur)) {
        setPosition((time / dur) * 100);
      }
    };

    const handleLoadedMetadata = () => {
      if (!isNaN(audio.duration)) {
        setDuration(audio.duration);
      }

      // Autoplay only after metadata loaded
      if (isPlaying) {
        const playPromise = audio.play();
        if (playPromise !== undefined) {
          playPromise.catch(() => {
            pauseTrack();
          });
        }
      }
    };

    const handleEnded = () => {
      const state = usePlayerStore.getState();
      if (state.repeatMode === "one") {
        // Repeat current track
        audio.currentTime = 0;
        audio.play().catch(() => {});
      } else if (state.queue.length > 0 || (state.repeatMode === "all" && state.history.length > 0)) {
        // Advance to next track in queue (or loop via history for repeat-all)
        state.nextTrack();
      } else {
        // Nothing left to play
        pauseTrack();
        audio.currentTime = 0;
        setPosition(0);
      }
    };

    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("ended", handleEnded);
    };
  }, [setPosition, setDuration, isPlaying, pauseTrack]);

  // Manual play/pause toggle
  const handlePlayClick = async () => {
    const audio = audioRef.current;
    if (!audio) return;

    try {
      if (isPlaying) {
        pauseTrack();
        audio.pause();
      } else {
        resumeTrack();
        const playPromise = audio.play();
        if (playPromise !== undefined) {
          await playPromise;
        }
      }
    } catch (error) {
      pauseTrack(); // fallback
    }
  };

  // Sync volume with audio element
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume;
    }
  }, [volume, isMuted]);

  // Sync audio currentTime with position when switching between players
  useEffect(() => {
    if (audioRef.current && duration > 0 && position > 0) {
      const targetTime = (position / 100) * duration;
      if (Math.abs(audioRef.current.currentTime - targetTime) > 1) {
        audioRef.current.currentTime = targetTime;
      }
    }
  }, [position, duration]);

  if (!currentTrack || isMinimized) return null;

  return (
    <>
      {/* Hidden audio element is now managed by PlayerProvider */}

      {/* Floating Mini Player */}
      <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50">
        <div
          className={cn(
            "backdrop-blur-md rounded-3xl shadow-2xl",
            "flex items-center gap-4 px-6 py-4 min-w-[500px]",
            isDarkMode
              ? "bg-zinc-900/95 border border-zinc-800"
              : "bg-white/95 border border-zinc-200"
          )}
        >
          {/* Cover Art */}
          <div className="relative">
            <div
              className={cn(
                "h-14 w-14 rounded-full overflow-hidden",
                isPlaying && "animate-rotate-smooth"
              )}
            >
              {currentTrack.cover_art_url ? (
                <img
                  src={currentTrack.cover_art_url}
                  alt={currentTrack.title}
                  className="h-full w-full object-cover"
                />
              ) : (
                <NoArtwork className="h-full w-full" iconSize="w-6 h-6" />
              )}
            </div>
            {/* {isPlaying && (
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
            )} */}
          </div>

          {/* Track Info */}
          <div className="flex-1 min-w-0">
            <div
              className={cn(
                "font-medium text-sm truncate",
                isDarkMode ? "text-white" : "text-zinc-900"
              )}
            >
              {currentTrack.title}
            </div>
            <div
              className={cn(
                "text-xs truncate",
                isDarkMode ? "text-zinc-400" : "text-zinc-600"
              )}
            >
              {currentTrack.artist}
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="icon"
              className={cn(
                "h-10 w-10 rounded-full",
                isDarkMode
                  ? "bg-white text-black hover:bg-zinc-200"
                  : "bg-zinc-900 text-white hover:bg-zinc-800"
              )}
              onClick={handlePlayClick}
              aria-label={isPlaying ? "Pause" : "Play"}
            >
              {isPlaying ? (
                <Pause className="h-5 w-5" />
              ) : (
                <Play className="h-5 w-5 ml-0.5" />
              )}
            </Button>
          </div>

          {/* Progress Bar */}
          <div className="flex items-center gap-2 min-w-[200px]">
            <div
              className={cn(
                "text-xs w-10 text-right",
                isDarkMode ? "text-zinc-400" : "text-zinc-600"
              )}
            >
              {formatTime(currentTime || 0)}
            </div>
            <Slider
              variant="theme-aware"
              aria-label="Seek"
              value={[position]}
              max={100}
              step={0.1}
              onValueChange={(values) => {
                setPosition(values[0]);
              }}
              onValueCommit={(values) => {
                if (audioRef.current && duration) {
                  audioRef.current.currentTime = (values[0] / 100) * duration;
                }
              }}
              className="flex-1"
            />
            <div
              className={cn(
                "text-xs w-10",
                isDarkMode ? "text-zinc-400" : "text-zinc-600"
              )}
            >
              {formatTime(duration || 0)}
            </div>
          </div>

          {/* Volume Control */}
          <div className="flex items-center gap-2 min-w-[120px]">
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                "h-8 w-8",
                isDarkMode
                  ? "text-white hover:bg-zinc-800"
                  : "text-zinc-700 hover:bg-zinc-100"
              )}
              onClick={toggleMute}
              aria-label={isMuted ? "Unmute" : "Mute"}
            >
              {isMuted ? (
                <VolumeX className="h-4 w-4" />
              ) : (
                <Volume2 className="h-4 w-4" />
              )}
            </Button>
            <Slider
              variant="theme-aware"
              aria-label="Volume"
              value={[isMuted ? 0 : volume * 100]}
              max={100}
              step={1}
              onValueChange={(values) => {
                const newVolume = values[0] / 100;
                setVolume(newVolume);
              }}
              className="flex-1"
            />
          </div>

          {/* Expand & Close Buttons */}
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              "h-8 w-8",
              isDarkMode
                ? "text-white hover:bg-zinc-800"
                : "text-zinc-700 hover:bg-zinc-100"
            )}
            onClick={toggleExpanded}
            aria-label="Expand player"
          >
            <Maximize2 className="h-4 w-4" />
          </Button>
          {/* Queue button removed */}
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              "h-8 w-8",
              isDarkMode
                ? "text-white hover:bg-zinc-800"
                : "text-zinc-700 hover:bg-zinc-100"
            )}
            onClick={() => {
              clearTrack();
              toggleMinimized();
              if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.currentTime = 0;
              }
            }}
            aria-label="Close player"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        {/* Queue panel removed */}
      </div>
    </>
  );
}
